/**
 * Organization + location model — pure helpers for the Organizations admin
 * pages: form state, validation, change diffing, linked-system flags,
 * country suggestions and currency-pair status. Tested in orgModel.test.js.
 */

export const DEFAULT_LOCATION_TIMEZONE = 'America/Chicago';

/** "15th", "1st", "22nd". */
export const ordinal = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1) return '—';
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
};

export const orgInitial = (org) => (String(org?.name || org?.code || '?').trim()[0] || '?').toUpperCase();

/** Keys whose values differ (JSON compare, so arrays/objects work). */
export const diffFields = (a = {}, b = {}, keys) => {
  const ks = keys || [...new Set([...Object.keys(a || {}), ...Object.keys(b || {})])];
  return ks.filter((k) => JSON.stringify(a?.[k] ?? null) !== JSON.stringify(b?.[k] ?? null));
};

// ── organization: general settings ──────────────────────────────────────────

export const generalFromOrg = (org) => ({
  name: org?.name || '',
  code: org?.code || '',
  reportingCurrency: org?.reportingCurrency || 'USD',
  financialCloseDay: org?.financialCloseDay ?? 15,
});

export const validateGeneral = (form) => {
  const errors = {};
  if (!String(form.name || '').trim()) errors.name = 'Name is required.';
  if (!String(form.code || '').trim()) errors.code = 'Code is required.';
  else if (/\s/.test(String(form.code).trim())) errors.code = 'Codes cannot contain spaces.';
  const day = Number(form.financialCloseDay);
  if (!Number.isInteger(day) || day < 1 || day > 31) errors.financialCloseDay = 'Enter a day from 1 to 31.';
  if (!/^[A-Z]{3}$/.test(String(form.reportingCurrency || ''))) errors.reportingCurrency = 'Pick a currency.';
  return errors;
};

/** Only the changed general fields, trimmed, for PUT /organizations. */
export const generalPatch = (original, form) => {
  const next = {
    name: String(form.name || '').trim(),
    code: String(form.code || '').trim(),
    reportingCurrency: form.reportingCurrency,
    financialCloseDay: Number(form.financialCloseDay),
  };
  const prev = { ...original, financialCloseDay: Number(original.financialCloseDay) };
  return diffFields(prev, next, Object.keys(next)).reduce((acc, k) => ({ ...acc, [k]: next[k] }), {});
};

// ── organization: policies (Cosmos org-config) ──────────────────────────────

export const MFA_METHODS = [
  { key: 'TOTP', label: 'Authenticator app', help: 'Google Authenticator, Authy, 1Password…' },
  { key: 'EMAIL', label: 'Email code', help: 'One-time code sent by email' },
  { key: 'SMS', label: 'Text message', help: 'One-time code sent by SMS' },
  { key: 'PASSKEY', label: 'Passkey or security key', help: 'Device passkey or hardware key' },
];

export const policiesFromConfig = (config) => {
  const mfa = config?.mfaPolicy || {};
  const cats = config?.journalAdjustmentCategories;
  return {
    mfaEnforced: Boolean(mfa.enforced),
    mfaMethods: Array.isArray(mfa.allowedMethods) && mfa.allowedMethods.length
      ? mfa.allowedMethods.map((m) => String(m).toUpperCase())
      : ['TOTP', 'EMAIL'],
    journalRestricted: Array.isArray(cats),
    journalCategories: Array.isArray(cats) ? [...new Set(cats.map((c) => String(c).toLowerCase()))] : [],
  };
};

export const validatePolicies = (form) => {
  const errors = {};
  if (form.mfaEnforced && !form.mfaMethods.length) errors.mfaMethods = 'Pick at least one sign-in method.';
  return errors;
};

/**
 * PATCH /orgConfig body pieces for the policies that changed.
 * journalAdjustmentCategories: null = every category allowed; [] = none.
 */
export const policiesPatch = (original, form) => {
  const out = {};
  const mfa = (p) => ({ enforced: Boolean(p.mfaEnforced), allowedMethods: [...p.mfaMethods].sort() });
  if (JSON.stringify(mfa(original)) !== JSON.stringify(mfa(form))) {
    out.mfaPolicy = { enforced: Boolean(form.mfaEnforced), allowedMethods: [...form.mfaMethods] };
  }
  const gate = (p) => (p.journalRestricted ? [...p.journalCategories].sort() : null);
  if (JSON.stringify(gate(original)) !== JSON.stringify(gate(form))) {
    out.journalAdjustmentCategories = gate(form);
  }
  return out;
};

export const mfaHelp = (methods = []) => {
  const labels = MFA_METHODS.filter((m) => methods.includes(m.key)).map((m) => m.label.toLowerCase());
  if (!labels.length) return 'Pick at least one sign-in method.';
  const list = labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
  return `${list.charAt(0).toUpperCase()}${list.slice(1)}. Users set it up at their next sign-in.`;
};

export const titleCase = (text) => String(text || '').replace(/\b\w/g, (c) => c.toUpperCase());

// ── locations ───────────────────────────────────────────────────────────────

export const LINKED_SYSTEMS = [
  { key: 'accountingId', badge: 'GL', label: 'Accounting ID' },
  { key: 'payrollId', badge: 'Payroll', label: 'Payroll ID' },
  { key: 'timekeepingId', badge: 'Time', label: 'Timekeeping ID' },
  { key: 'lighthouseId', badge: 'Lighthouse', label: 'Lighthouse ID' },
  { key: 'googlePlaceId', badge: 'Google', label: 'Google Place ID' },
];

export const linkedSystemFlags = (location) => LINKED_SYSTEMS.map((s) => {
  const value = location?.[s.key];
  const linked = value !== null && value !== undefined && String(value).trim() !== '';
  return { ...s, linked, value: linked ? String(value).trim() : null };
});

export const cityLine = (location) => [location?.city, location?.state]
  .map((v) => (v === null || v === undefined ? '' : String(v).trim()))
  .filter(Boolean)
  .join(', ');

export const COUNTRY_DEFAULT_CURRENCY = { US: 'USD', CA: 'CAD', JP: 'JPY', GB: 'GBP', AU: 'AUD', MX: 'MXN', SG: 'SGD' };
export const COUNTRY_DEFAULT_TIMEZONE = {
  US: DEFAULT_LOCATION_TIMEZONE,
  CA: 'America/Toronto',
  JP: 'Asia/Tokyo',
  GB: 'Europe/London',
  AU: 'Australia/Sydney',
  MX: 'America/Mexico_City',
  SG: 'Asia/Singapore',
};
const COUNTRY_NAMES = {
  'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', USA: 'US', 'U.S.': 'US', 'U.S.A.': 'US',
  CANADA: 'CA', JAPAN: 'JP', 'UNITED KINGDOM': 'GB', UK: 'GB', 'GREAT BRITAIN': 'GB', ENGLAND: 'GB',
  AUSTRALIA: 'AU', MEXICO: 'MX', SINGAPORE: 'SG',
};

/** ISO-3166 alpha-2 code for a typed country ("US", "usa", "Japan"), or null. */
export const countryCode = (value) => {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return null;
  if (COUNTRY_DEFAULT_CURRENCY[text]) return text;
  return COUNTRY_NAMES[text] || null;
};

export const suggestedCurrencyFor = (country) => COUNTRY_DEFAULT_CURRENCY[countryCode(country)] || null;

export const LOCATION_FIELDS = [
  'name', 'code', 'address', 'city', 'state', 'zip', 'country', 'timezone', 'currency',
  'phoneNumber', 'websiteUrl',
  'accountingId', 'payrollId', 'timekeepingId', 'lighthouseId', 'googlePlaceId',
  'latitude', 'longitude', 'wheelchairAccessible',
];

const str = (v) => (v === null || v === undefined ? '' : String(v));

export const locationFormFromRecord = (loc) => ({
  name: str(loc?.name),
  code: str(loc?.code),
  address: str(loc?.address),
  city: str(loc?.city),
  state: str(loc?.state),
  zip: str(loc?.zip),
  country: str(loc?.country),
  timezone: loc?.timezone || DEFAULT_LOCATION_TIMEZONE,
  currency: loc?.currency || '',
  phoneNumber: str(loc?.phoneNumber),
  websiteUrl: str(loc?.websiteUrl),
  accountingId: str(loc?.accountingId),
  payrollId: str(loc?.payrollId),
  timekeepingId: str(loc?.timekeepingId),
  lighthouseId: str(loc?.lighthouseId),
  googlePlaceId: str(loc?.googlePlaceId),
  latitude: str(loc?.latitude),
  longitude: str(loc?.longitude),
  wheelchairAccessible: Boolean(loc?.wheelchairAccessible),
});

/**
 * Apply a field change with the old modal's suggestions: picking a country
 * fills an EMPTY currency (and a default timezone) unless the admin already
 * chose one in this session.
 *   touched: { currency?: bool, timezone?: bool }
 * Returns { form, suggested: { currency?: code, timezone?: tz } }.
 */
export const applyLocationChange = (prev, field, value, touched = {}) => {
  const form = { ...prev, [field]: value };
  const suggested = {};
  if (field === 'country') {
    const code = countryCode(value);
    if (code && !touched.currency && !prev.currency) {
      form.currency = COUNTRY_DEFAULT_CURRENCY[code];
      suggested.currency = form.currency;
    }
    if (code && !touched.timezone && (!prev.timezone || prev.timezone === DEFAULT_LOCATION_TIMEZONE)) {
      const tz = COUNTRY_DEFAULT_TIMEZONE[code];
      if (tz && tz !== prev.timezone) {
        form.timezone = tz;
        suggested.timezone = tz;
      }
    }
  }
  return { form, suggested };
};

export const validateLocation = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Name is required.';
  if (!form.code.trim()) errors.code = 'Code is required.';
  if (!form.timezone) errors.timezone = 'Timezone is required.';
  if (form.latitude.trim() !== '') {
    const lat = Number(form.latitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) errors.latitude = 'Must be between -90 and 90.';
  }
  if (form.longitude.trim() !== '') {
    const lng = Number(form.longitude);
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) errors.longitude = 'Must be between -180 and 180.';
  }
  if (form.websiteUrl.trim() && !/^https?:\/\//i.test(form.websiteUrl.trim())) errors.websiteUrl = 'Start with http:// or https://';
  return errors;
};

/** API payload for every location field (blank → null). */
export const locationPayload = (form) => {
  const text = (v) => (String(v || '').trim() || null);
  return {
    name: form.name.trim(),
    code: form.code.trim(),
    address: text(form.address),
    city: text(form.city),
    state: text(form.state),
    zip: text(form.zip),
    country: text(form.country),
    timezone: form.timezone || DEFAULT_LOCATION_TIMEZONE,
    currency: form.currency || null,
    phoneNumber: text(form.phoneNumber),
    websiteUrl: text(form.websiteUrl),
    accountingId: text(form.accountingId),
    payrollId: text(form.payrollId),
    timekeepingId: text(form.timekeepingId),
    lighthouseId: text(form.lighthouseId),
    googlePlaceId: text(form.googlePlaceId),
    latitude: form.latitude.trim() === '' ? null : Number(form.latitude),
    longitude: form.longitude.trim() === '' ? null : Number(form.longitude),
    wheelchairAccessible: Boolean(form.wheelchairAccessible),
  };
};

/** Only the fields that changed (PUT /locations writes only present keys). */
export const locationPatch = (originalForm, form) => {
  const prev = locationPayload(originalForm);
  const next = locationPayload(form);
  return diffFields(prev, next, LOCATION_FIELDS).reduce((acc, k) => ({ ...acc, [k]: next[k] }), {});
};

export const locationChangeCount = (originalForm, form) => diffFields(originalForm, form, LOCATION_FIELDS).length;

// ── currencies ──────────────────────────────────────────────────────────────

export const isTruthyFlag = (v) => v === true || v === 1 || v === '1';

export const currencyOptions = (currencies = [], extras = []) => {
  const map = new Map();
  const add = (c) => {
    const code = String(typeof c === 'string' ? c : c?.code || '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code) || map.has(code)) return;
    const name = typeof c === 'object' && c?.name && c.name !== code ? c.name : null;
    map.set(code, { value: code, label: name ? `${code} · ${name}` : code });
  };
  currencies.forEach(add);
  extras.forEach(add);
  return [...map.values()].sort((a, b) => a.value.localeCompare(b.value));
};

export const dateKey = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 10) : null;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatDateKey = (key, { year = false } = {}) => {
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}${year ? `, ${y}` : ''}`;
};
export const formatMonthKey = (key) => {
  if (!key) return '—';
  const [y, m] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

export const formatRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1) return n.toFixed(4);
  return Number(n.toPrecision(4)).toString();
};

const daysApart = (fromKey, toKey) => Math.round((new Date(`${toKey}T00:00:00Z`) - new Date(`${fromKey}T00:00:00Z`)) / 86400000);

/**
 * Status column for a currency pair.
 *   tone: ok | info | warn | muted
 */
export const pairStatus = (pair, today = new Date().toISOString().slice(0, 10)) => {
  if (!isTruthyFlag(pair?.isActive) && pair?.isActive !== undefined) return { tone: 'muted', text: 'Removed' };
  const auto = isTruthyFlag(pair?.autoDailyEnabled);
  const latest = dateKey(pair?.latestRateDate);
  const earliest = dateKey(pair?.earliestRateDate);
  const backfillStart = dateKey(pair?.autoBackfillStartDate);
  const since = backfillStart ? formatMonthKey(backfillStart) : null;
  if (!latest) {
    return auto
      ? { tone: 'info', text: since ? `Backfilling since ${since}…` : 'Waiting for first rate…' }
      : { tone: 'warn', text: 'No rates yet' };
  }
  if (auto && backfillStart && earliest && daysApart(backfillStart, earliest) > 7) {
    return { tone: 'info', text: `Backfilling since ${since}…` };
  }
  const granularity = String(pair?.latestGranularity || '').toLowerCase();
  if (String(pair?.latestRateSource || '').toUpperCase().startsWith('MANUAL')) {
    return { tone: 'muted', text: `Manual${granularity ? `, ${granularity}` : ''}` };
  }
  if (auto) {
    return daysApart(latest, today) <= 5
      ? { tone: 'ok', text: 'Current' }
      : { tone: 'warn', text: `Stale · last ${formatDateKey(latest)}` };
  }
  return { tone: 'muted', text: `${granularity ? `${granularity.charAt(0).toUpperCase()}${granularity.slice(1)}` : 'Rate'} · last ${formatDateKey(latest)}` };
};

/** rateDate the API expects for a manual rate: monthly = 1st, yearly = Jan 1. */
export const manualRateDate = (granularity, value) => {
  const text = String(value || '').trim();
  if (granularity === 'YEARLY') return /^\d{4}$/.test(text) ? `${text}-01-01` : null;
  if (granularity === 'MONTHLY') return /^\d{4}-\d{2}$/.test(text) ? `${text}-01` : (/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text.slice(0, 7)}-01` : null);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};
